import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { clientesClient, type Client } from '../../services/clientesClient'
import { ApiClientError } from '../../services/apiClient'
import { useAuth } from '../../auth/AuthContext'
import { ClientDetailModal } from './ClientDetailModal'
import { ClientFormModal } from './ClientFormModal'

type Mode =
  | { kind: 'list' }
  | { kind: 'detail'; client: Client }
  | { kind: 'edit'; client: Client }
  | { kind: 'create' }

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 250

/**
 * Pantalla principal de clientes (§8 SRS).
 *
 * Cubre:
 *   - CLI-01: 25 registros iniciales + lazy load al hacer scroll.
 *   - CLI-02: banner ">25 registros" cuando aplica.
 *   - CLI-03: filtro tiempo real con debounce; vacío vuelve a la lista paginada.
 *   - CLI-05/06: detalle, edición y alta en modales (sin salir de la pantalla).
 *   - CLI-07: el endpoint /clientes/search alimentará el autocompletado de reportes (milestone 7).
 *
 * Estrategia de búsqueda:
 *   - Query vacía → lista paginada (cursor based).
 *   - Query con texto → llama /search (LIKE alias + hash exacto razón social/RFC).
 *     La lista se reemplaza por los resultados de búsqueda. El intersection observer
 *     se desactiva en modo búsqueda.
 */
export function ClientesPage() {
  const { state: authState } = useAuth()
  const isOwner = authState.status === 'authenticated' && authState.user.role === 'owner'

  const [items, setItems] = useState<Client[]>([])
  const [cursor, setCursor] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [totalEstimate, setTotalEstimate] = useState<number | null>(null)

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [searching, setSearching] = useState(false)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })

  const sentinelRef = useRef<HTMLDivElement | null>(null)

  // ─── Cargar primera página ────────────────────────────────────────────────
  const fetchInitialPage = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const page = await clientesClient.list(null, PAGE_SIZE)
      setItems(page.items)
      setCursor(page.next_cursor)
      setHasMore(page.next_cursor !== null)
      setTotalEstimate(page.total)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar la lista.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchInitialPage()
  }, [fetchInitialPage])

  // ─── Debounce de búsqueda ─────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [searchInput])

  // ─── Ejecutar búsqueda o volver a paginated ──────────────────────────────
  useEffect(() => {
    let cancelled = false

    if (debouncedSearch === '') {
      setSearching(false)
      // Volver a la lista paginada si no estamos ya en ella (recargar primera página).
      fetchInitialPage()
      return
    }

    setSearching(true)
    setLoading(true)
    setError(null)
    clientesClient
      .search(debouncedSearch)
      .then((res) => {
        if (cancelled) return
        setItems(res.items)
        setHasMore(false)
        setCursor(null)
      })
      .catch((err) => {
        if (cancelled) return
        setError(
          err instanceof ApiClientError ? err.apiError.message : 'No se pudo realizar la búsqueda.',
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedSearch, fetchInitialPage])

  // ─── Lazy load: intersection observer del sentinel ────────────────────────
  useEffect(() => {
    if (searching || !hasMore || cursor === null) return
    const node = sentinelRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && hasMore && cursor !== null) {
          loadNextPage(cursor)
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, hasMore, loading, searching])

  async function loadNextPage(fromCursor: number) {
    setLoading(true)
    try {
      const page = await clientesClient.list(fromCursor, PAGE_SIZE)
      setItems((prev) => [...prev, ...page.items])
      setCursor(page.next_cursor)
      setHasMore(page.next_cursor !== null)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar más.')
    } finally {
      setLoading(false)
    }
  }

  // ─── Mutaciones (alta, edición, borrado) ──────────────────────────────────
  const handleSaved = useCallback((saved: Client) => {
    setItems((prev) => {
      const idx = prev.findIndex((c) => c.id === saved.id)
      if (idx === -1) return [saved, ...prev]
      const next = [...prev]
      next[idx] = saved
      return next
    })
    setMode({ kind: 'detail', client: saved })
  }, [])

  const handleDeleted = useCallback((id: number) => {
    setItems((prev) => prev.filter((c) => c.id !== id))
    setMode({ kind: 'list' })
    setTotalEstimate((t) => (t === null ? null : Math.max(0, t - 1)))
  }, [])

  // ─── Render ──────────────────────────────────────────────────────────────
  const showBanner = useMemo(() => {
    return !searching && totalEstimate !== null && totalEstimate > PAGE_SIZE
  }, [searching, totalEstimate])

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Clientes</h1>
          {totalEstimate !== null && !searching && (
            <span className="muted">{totalEstimate.toLocaleString('es-MX')} en total</span>
          )}
          {searching && (
            <span className="muted">{items.length} resultado(s) para “{debouncedSearch}”</span>
          )}
        </div>
        <div className="list-toolbar__actions">
          <input
            type="search"
            className="search-input"
            placeholder="Buscar por alias, razón social o RFC…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Buscar clientes"
          />
          {isOwner && (
            <button
              type="button"
              className="primary-btn"
              onClick={() => setMode({ kind: 'create' })}
            >
              + Nuevo cliente
            </button>
          )}
        </div>
      </header>

      {showBanner && (
        <p className="banner">
          Mostrando los primeros {PAGE_SIZE} de {totalEstimate?.toLocaleString('es-MX')} clientes.
          Desplázate para cargar más o usa el buscador.
        </p>
      )}

      {error && <p className="error">{error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Alias</th>
              <th scope="col">Razón social</th>
              <th scope="col">RFC</th>
              <th scope="col" aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className="data-table__empty">
                  {searching
                    ? 'No se encontraron clientes que coincidan con la búsqueda.'
                    : 'Aún no hay clientes. Crea el primero con el botón “Nuevo cliente”.'}
                </td>
              </tr>
            )}

            {items.map((client) => (
              <tr
                key={client.id}
                onClick={() => setMode({ kind: 'detail', client })}
                className="data-table__row"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setMode({ kind: 'detail', client })
                  }
                }}
              >
                <td><strong>{client.alias}</strong></td>
                <td>{client.legal_name}</td>
                <td className="mono">{client.tax_id}</td>
                <td className="data-table__actions" onClick={(e) => e.stopPropagation()}>
                  {isOwner ? (
                    <button
                      type="button"
                      className="ghost-btn small"
                      onClick={() => setMode({ kind: 'edit', client })}
                    >
                      Editar
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ghost-btn small"
                      onClick={() => setMode({ kind: 'detail', client })}
                    >
                      Ver
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && <p className="muted center">Cargando…</p>}
        {!searching && hasMore && <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />}
        {!loading && !hasMore && items.length > 0 && !searching && (
          <p className="muted center">Has llegado al final de la lista.</p>
        )}
      </div>

      {/* ─── Modales ────────────────────────────────────────────────────── */}
      <ClientDetailModal
        open={mode.kind === 'detail'}
        client={mode.kind === 'detail' ? mode.client : null}
        onClose={() => setMode({ kind: 'list' })}
        onEdit={() => mode.kind === 'detail' && setMode({ kind: 'edit', client: mode.client })}
        onDeleted={handleDeleted}
        canEdit={isOwner}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <ClientFormModal
          key={mode.kind === 'edit' ? mode.client.id : 'new'}
          open
          mode={mode.kind}
          client={mode.kind === 'edit' ? mode.client : null}
          onClose={() => setMode({ kind: 'list' })}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}
